/* Compact vertical timeline of a student's academic path.
 * Consumes GET /api/students/{id}/timeline. Merges cursus + auto history +
 * year-end decisions + current enrollment.
 */
import { useEffect, useState } from 'react';
import { cursusApi } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import AttestationMenu from './AttestationMenu';
import {
  Star, ArrowsClockwise, Archive, GraduationCap, Circle, Clock,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const STATUS_ICON = {
  promu: { icon: Star, tone: 'text-emerald-600', label: 'Promu(e) ★' },
  admis: { icon: Star, tone: 'text-blue-600', label: 'Admis(e) ★' },
  redoublé: { icon: ArrowsClockwise, tone: 'text-amber-600', label: 'Redoublé ↺' },
  transféré: { icon: Archive, tone: 'text-slate-600', label: 'Transféré 📦' },
  quitté: { icon: Archive, tone: 'text-red-600', label: 'Quitté 📦' },
  active: { icon: Clock, tone: 'text-[#0052FF]', label: 'En cours' },
};

const SOURCE_TAG = {
  manual: { label: 'Saisie manuelle', tone: 'bg-indigo-50 text-indigo-700' },
  auto: { label: 'Auto — pipeline', tone: 'bg-emerald-50 text-emerald-700' },
  decision: { label: 'Décision conseil', tone: 'bg-amber-50 text-amber-700' },
  current: { label: 'Année en cours', tone: 'bg-blue-50 text-blue-700' },
};

const StudentTimeline = ({ studentId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data: d } = await cursusApi.timeline(studentId);
        setData(d);
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Erreur chargement timeline');
      } finally { setLoading(false); }
    })();
  }, [studentId]);

  if (loading) return null;
  if (!data || !data.items.length) return null;

  const student = {
    id: data.student_id,
    first_name: data.student_name?.split(' ').slice(1).join(' ') || '',
    last_name: data.student_name?.split(' ')[0] || '',
    school_year: data.current_school_year,
    class_id: data.current_class,
  };

  return (
    <Card className="border border-[#E2E8F0] shadow-none" data-testid="student-timeline-card">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="font-outfit text-base flex items-center gap-2">
          <GraduationCap size={18} className="text-[#0052FF]" weight="duotone" />
          Historique de l&apos;élève
        </CardTitle>
        <AttestationMenu student={student} />
      </CardHeader>
      <CardContent>
        <div className="relative pl-6">
          {/* Vertical line */}
          <div className="absolute left-2 top-1 bottom-1 w-px bg-[#E2E8F0]" />
          <div className="space-y-4">
            {data.items.map((it, idx) => {
              const cfg = STATUS_ICON[it.status] || { icon: Circle, tone: 'text-[#94A3B8]', label: '—' };
              const Icon = cfg.icon;
              const src = SOURCE_TAG[it.source] || SOURCE_TAG.manual;
              return (
                <div key={it.school_year + '-' + idx} className="relative"
                     data-testid={`timeline-item-${it.school_year}`}>
                  <div className={`absolute -left-6 top-0 w-4 h-4 rounded-full bg-white border-2 ${
                    idx === 0 ? 'border-[#0052FF]' : 'border-[#E2E8F0]'
                  } flex items-center justify-center`}>
                    <Icon size={10} weight="fill" className={cfg.tone} />
                  </div>
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="font-outfit font-bold text-sm text-[#0F172A]">
                      {it.school_year}
                    </span>
                    <span className="text-sm text-[#475569]">·</span>
                    <span className="font-medium text-sm">{it.class_name}</span>
                    {it.status && (
                      <span className={`text-[11px] ${cfg.tone} font-medium`}>
                        · {cfg.label}
                      </span>
                    )}
                    {it.next_class && it.next_class !== it.class_name && (
                      <span className="text-[11px] text-[#64748B]">
                        → {it.next_class}
                      </span>
                    )}
                    <span className={`ml-auto text-[10px] px-2 py-0.5 rounded-full ${src.tone}`}>
                      {src.label}
                    </span>
                  </div>
                  {(it.average != null || it.rank != null || it.comment) && (
                    <div className="text-xs text-[#64748B] mt-1 flex gap-3 flex-wrap">
                      {it.average != null && (
                        <span>Moyenne : <b>{Number(it.average).toFixed(2)}/20</b></span>
                      )}
                      {it.rank != null && <span>Rang : <b>{it.rank}</b></span>}
                      {it.comment && <span className="italic">« {it.comment} »</span>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default StudentTimeline;
